/**
 * @file Turns one `SourceCellTable` into the rows a table chunk carries, plus
 * each row's cell citations keyed by the same source column. The one place a
 * `sourceCells` staging's columns are named, so `loadParsedChunks` (propose and
 * apply), `rowSourceCitations` (apply) and the Mapping Review grid (React)
 * always agree on keys and row order.
 * Invariants: rows are the distinct `SourceCell.row` numbers in ascending
 * order; columns are named once per table by `SourceCell.column`, never by a
 * cell's position in its row, so a row that leaves out a cell keeps its other
 * cells in place. A column is named by its printed header, `Column <column+1>`
 * when the header is empty, and a repeat across the table's columns gets
 * ` (2)`, ` (3)` … in column order; an empty value is `null`; a cell citing no
 * word has no citation. `cellKeys` is index-aligned to `table.cells` and names
 * each cell's row index and source column, the keys the decisions overlay's
 * `cellOverrides` use.
 */
import type { SourceCellTable, SourceCitation } from './System.Agent.PdfEvidence.types';

export function sourceCellRows(fileSha256: string, table: SourceCellTable): {
    sourceColumns: string[];
    rows: Record<string, string | null>[];
    citations: Record<string, SourceCitation>[];
    cellKeys: { rowIndex: number; sourceColumn: string }[];
} {
    const columnIndexes = [...new Set(table.cells.map((cell) => cell.column))].sort((a, b) => a - b);
    const bases = columnIndexes.map((column) =>
        table.cells.find((cell) => cell.column === column)?.columnHeader.trim() || `Column ${column + 1}`);
    const sourceColumns = bases.map((base, position) => {
        const repeat = bases.slice(0, position).filter((other) => other === base).length;
        return repeat === 0 ? base : `${base} (${repeat + 1})`;
    });
    const nameByColumn = new Map(columnIndexes.map((column, position) => [column, sourceColumns[position]] as const));
    const rowNumbers = [...new Set(table.cells.map((cell) => cell.row))].sort((a, b) => a - b);
    const keyedRows = rowNumbers.map((rowNumber) => table.cells
        .filter((cell) => cell.row === rowNumber)
        .map((cell) => ({ key: nameByColumn.get(cell.column) ?? '', cell })));
    return {
        sourceColumns,
        rows: keyedRows.map((row) => Object.fromEntries(
            sourceColumns.map((column) => {
                const value = row.find(({ key }) => key === column)?.cell.value ?? '';
                return [column, value === '' ? null : value];
            }),
        )),
        citations: keyedRows.map((row) => Object.fromEntries(
            row
                .filter(({ cell }) => cell.citedWordIds.length > 0)
                .map(({ key, cell }) => [key, { fileSha256, page: table.page, citedWordIds: cell.citedWordIds, box: cell.box }]),
        )),
        cellKeys: table.cells.map((cell) => ({
            rowIndex: rowNumbers.indexOf(cell.row),
            sourceColumn: nameByColumn.get(cell.column) ?? '',
        })),
    };
}
